import {
  ArrowHelper,
  ConeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  Scene,
  SphereGeometry,
  Vector3,
} from 'three'
import type { Pilot } from '../physics/Pilot'

export interface DebugFlags {
  thrustVectors: boolean
  gravityVector: boolean
  centreOfMass: boolean
  angularVelocity: boolean
  exhaust: boolean
}

export const DEFAULT_DEBUG_FLAGS: DebugFlags = {
  thrustVectors: true,
  gravityVector: true,
  centreOfMass: true,
  angularVelocity: true,
  exhaust: true,
}

const WORLD_DOWN = new Vector3(0, -1, 0)
/** Metres of arrow per newton — a hover rocket is ~1.5 m of arrow. */
const NEWTON_SCALE = 0.0027

/**
 * Debug overlays, built alongside the physics rather than after it
 * (DESIGN.md §3). Two jobs: tuning is blind without them, and the
 * room-gravity / world-gravity mismatch (§2a) means the player genuinely
 * benefits from *seeing* which way virtual-down points. The gravity arrow is
 * the prototype of a diegetic helmet attitude indicator.
 */
export class DebugOverlay {
  readonly group = new Group()
  readonly flags: DebugFlags = { ...DEFAULT_DEBUG_FLAGS }

  private readonly thrustArrows: ArrowHelper[] = []
  private readonly gravityArrow: ArrowHelper
  private readonly omegaArrow: ArrowHelper
  private readonly comMarker: Mesh
  private readonly exhaust: Mesh[] = []

  constructor(scene: Scene) {
    this.group.renderOrder = 10

    for (let i = 0; i < 2; i++) {
      const arrow = new ArrowHelper(
        new Vector3(0, 1, 0),
        new Vector3(),
        1,
        i === 0 ? 0xff8a5c : 0x6fd0ff,
        0.28,
        0.16,
      )
      this.thrustArrows.push(arrow)
      this.group.add(arrow)

      // Exhaust cone points the opposite way to the thrust — out of the nozzle.
      const cone = new Mesh(
        new ConeGeometry(0.11, 1, 10, 1, true),
        new MeshBasicMaterial({
          color: i === 0 ? 0xffb07a : 0x9fe0ff,
          transparent: true,
          opacity: 0.75,
          depthWrite: false,
        }),
      )
      this.exhaust.push(cone)
      this.group.add(cone)
    }

    this.gravityArrow = new ArrowHelper(WORLD_DOWN, new Vector3(), 3, 0xff6b6b, 0.5, 0.28)
    this.omegaArrow = new ArrowHelper(new Vector3(0, 1, 0), new Vector3(), 1, 0xc792ea, 0.3, 0.18)
    this.group.add(this.gravityArrow, this.omegaArrow)

    this.comMarker = new Mesh(
      new SphereGeometry(0.11, 12, 8),
      new MeshBasicMaterial({ color: 0xffe066, transparent: true, opacity: 0.9 }),
    )
    this.group.add(this.comMarker)

    scene.add(this.group)
  }

  update(pilot: Pilot): void {
    const com = pilot.body.position

    for (let i = 0; i < 2; i++) {
      const rocket = pilot.debug.rockets[i]
      const arrow = this.thrustArrows[i]
      const cone = this.exhaust[i]
      const visible = this.flags.thrustVectors && rocket.force > 1

      arrow.visible = visible
      if (visible) {
        arrow.position.copy(rocket.origin)
        arrow.setDirection(rocket.direction)
        arrow.setLength(Math.max(0.2, rocket.force * NEWTON_SCALE), 0.28, 0.16)
      }

      const exhaustVisible = this.flags.exhaust && rocket.level > 0.01
      cone.visible = exhaustVisible
      if (exhaustVisible) {
        const length = 0.35 + rocket.level * 2.2
        cone.scale.set(1, length, 1)
        // Cone geometry points +Y; aim it along −thrust, i.e. out the nozzle.
        _down.copy(rocket.direction).multiplyScalar(-1)
        cone.quaternion.setFromUnitVectors(_up, _down)
        cone.position.copy(rocket.origin).addScaledVector(_down, length * 0.5)
        ;(cone.material as MeshBasicMaterial).opacity = 0.35 + rocket.level * 0.5
      }
    }

    this.gravityArrow.visible = this.flags.gravityVector
    if (this.flags.gravityVector) {
      this.gravityArrow.position.copy(com)
      this.gravityArrow.setDirection(WORLD_DOWN)
      this.gravityArrow.setLength(3 * Math.max(0.15, pilot.cfg.gravityScale), 0.5, 0.28)
    }

    const omega = pilot.body.omega
    const omegaLength = omega.length()
    this.omegaArrow.visible = this.flags.angularVelocity && omegaLength > 0.05
    if (this.omegaArrow.visible) {
      this.omegaArrow.position.copy(com)
      _dir.copy(omega).divideScalar(omegaLength)
      this.omegaArrow.setDirection(_dir)
      this.omegaArrow.setLength(Math.min(4, omegaLength * 0.6), 0.3, 0.18)
    }

    this.comMarker.visible = this.flags.centreOfMass
    this.comMarker.position.copy(com)
  }
}

const _up = new Vector3(0, 1, 0)
const _down = new Vector3()
const _dir = new Vector3()
